import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Key, BarChart3, LogOut, Plus, 
  Search, Filter, MoreVertical, Eye, Pause, Play,
  ArrowUpRight, Copy, Check, AlertCircle, RefreshCw,
  Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const API_URL = '';

export default function MasterDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Auth state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Data state
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [codes, setCodes] = useState([]);
  const [tiers, setTiers] = useState({});
  const [platformHealth, setPlatformHealth] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Dialog state
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showGenerateCodes, setShowGenerateCodes] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  
  // Form state
  const [newTenant, setNewTenant] = useState({
    name: '', slug: '', owner_email: '', owner_name: '', tier: 'tier1'
  });
  const [codeGeneration, setCodeGeneration] = useState({ tier: 'tier1', count: 10 });

  const getToken = () => localStorage.getItem('master_token');

  const fetchWithAuth = async (url, options = {}) => {
    const token = getToken();
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });
    if (response.status === 401) {
      localStorage.removeItem('master_token');
      setIsAuthenticated(false);
      throw new Error('Unauthorized');
    }
    return response;
  };

  useEffect(() => {
    const token = getToken();
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/stats`);
      if (response.ok) {
        setIsAuthenticated(true);
        loadAllData();
      } else {
        localStorage.removeItem('master_token');
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch(`${API_URL}/api/master/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('master_token', data.access_token);
        setIsAuthenticated(true);
        loadAllData();
      } else {
        setLoginError(data.detail || 'Login failed');
      }
    } catch (err) {
      setLoginError('Connection error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('master_token');
    setIsAuthenticated(false);
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadStats(), loadTenants(), loadCodes(), loadTiers(), loadPlatformHealth(), loadSchedulerStatus()]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/stats`);
      if (response.ok) setStats(await response.json());
    } catch (err) { console.error('Failed to load stats:', err); }
  };

  const loadTenants = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/tenants?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
      }
    } catch (err) { console.error('Failed to load tenants:', err); }
  };

  const loadCodes = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/appsumo/codes?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setCodes(data.codes || []);
      }
    } catch (err) { console.error('Failed to load codes:', err); }
  };

  const loadTiers = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/tiers`);
      if (response.ok) setTiers(await response.json());
    } catch (err) { console.error('Failed to load tiers:', err); }
  };

  const loadPlatformHealth = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/scheduled-jobs/reports/platform-usage`);
      if (response.ok) setPlatformHealth(await response.json());
    } catch (err) { console.error('Failed to load platform health:', err); }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/scheduled-jobs/limit-check/status`);
      if (response.ok) setSchedulerStatus(await response.json());
    } catch (err) { console.error('Failed to load scheduler status:', err); }
  };

  const runLimitCheckNow = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/scheduled-jobs/limit-check/run-now`, {
        method: 'POST'
      });
      if (response.ok) {
        const result = await response.json();
        alert(`Limit check completed!\n\nTenants checked: ${result.result.tenants_checked}\nWarnings sent: ${result.result.warnings_sent}\nAlerts sent: ${result.result.alerts_sent}`);
        loadPlatformHealth();
        loadSchedulerStatus();
      }
    } catch (err) { console.error('Failed to run limit check:', err); }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/tenants`, {
        method: 'POST',
        body: JSON.stringify(newTenant)
      });
      const data = await response.json();
      if (response.ok) {
        setShowCreateTenant(false);
        setNewTenant({ name: '', slug: '', owner_email: '', owner_name: '', tier: 'tier1' });
        loadAllData();
        alert(`Tenant created! Temp password: ${data.temp_password}`);
      } else {
        alert(data.detail || 'Failed to create tenant');
      }
    } catch (err) {
      alert('Error creating tenant');
    }
  };

  const handleGenerateCodes = async (e) => {
    e.preventDefault();
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/appsumo/generate`, {
        method: 'POST',
        body: JSON.stringify(codeGeneration)
      });
      const data = await response.json();
      if (response.ok) {
        setGeneratedCodes(data.codes || []);
        loadCodes();
        loadStats();
      } else {
        alert(data.detail || 'Failed to generate codes');
      }
    } catch (err) {
      alert('Error generating codes');
    }
  };

  const handleSuspendTenant = async (tenantId, currentStatus) => {
    const action = currentStatus === 'suspended' ? 'reactivate' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} this tenant?`)) return;
    
    try {
      const response = await fetchWithAuth(`${API_URL}/api/master/tenants/${tenantId}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Admin action' })
      });
      if (response.ok) {
        loadTenants();
        loadStats();
      }
    } catch (err) {
      alert(`Failed to ${action} tenant`);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getTierDisplayName = (tier) => {
    switch (tier) {
      case 'tier1': return 'Starter';
      case 'tier2': return 'Growth';
      case 'tier3': return 'Scale';
      case 'owner': return 'Platform Owner';
      default: return tier;
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'tier1': return 'bg-slate-100 text-slate-700';
      case 'tier2': return 'bg-blue-100 text-blue-700';
      case 'tier3': return 'bg-purple-100 text-purple-700';
      case 'owner': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <CardTitle className="text-2xl">Master Admin</CardTitle>
            <CardDescription>Edrano Platform Administration</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {loginError}
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <Input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="master@edrano.my"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Password</label>
                <Input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-indigo-500 to-violet-600">
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-slate-900">Master Admin</h1>
                <p className="text-xs text-slate-500">Edrano Platform</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={loadAllData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="tenants" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="codes" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              AppSumo Codes
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Total Tenants</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{stats?.tenants?.total || 0}</div>
                  <p className="text-xs text-green-600 mt-1">
                    {stats?.tenants?.recent_30d || 0} new this month
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">Active Tenants</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">{stats?.tenants?.active || 0}</div>
                  <p className="text-xs text-slate-500 mt-1">
                    {stats?.tenants?.suspended || 0} suspended
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">AppSumo Codes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-indigo-600">{stats?.appsumo_codes?.available || 0}</div>
                  <p className="text-xs text-slate-500 mt-1">
                    {stats?.appsumo_codes?.redeemed || 0} redeemed
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">By Tier</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {Object.entries(stats?.tenants?.by_tier || {}).map(([tier, count]) => (
                      <Badge key={tier} className={getTierBadgeColor(tier)}>
                        {getTierDisplayName(tier)}: {count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" onClick={() => setShowCreateTenant(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Tenant
                  </Button>
                  <Button className="w-full justify-start" variant="outline" onClick={() => setShowGenerateCodes(true)}>
                    <Key className="w-4 h-4 mr-2" />
                    Generate AppSumo Codes
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Tier Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(tiers).map(([tier, config]) => (
                      <div key={tier} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className={getTierBadgeColor(tier)}>
                            {getTierDisplayName(tier)}
                          </Badge>
                          <span className="text-sm text-slate-600">
                            {config.limits.max_clients >= 999999 ? '∞' : config.limits.max_clients} clients
                          </span>
                        </div>
                        <span className="font-semibold text-indigo-600">
                          {config.platform_fee_percent}% fee
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Platform Health Section */}
            <div className="mt-8">
              <Card className="border-2 border-indigo-100">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-violet-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-indigo-600" />
                        Platform Health Dashboard
                      </CardTitle>
                      <CardDescription>
                        Real-time overview of tenant resource usage and upgrade opportunities
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={loadPlatformHealth}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                      <Button size="sm" onClick={runLimitCheckNow} className="bg-indigo-600 hover:bg-indigo-700">
                        <Play className="w-4 h-4 mr-2" />
                        Run Check Now
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {platformHealth ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {/* Total Resources */}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="text-sm text-slate-500 mb-1">Total Clients</div>
                        <div className="text-2xl font-bold text-slate-900">{platformHealth.resource_totals?.clients || 0}</div>
                        <div className="text-xs text-slate-400 mt-1">across all tenants</div>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="text-sm text-slate-500 mb-1">Total Services</div>
                        <div className="text-2xl font-bold text-slate-900">{platformHealth.resource_totals?.services || 0}</div>
                        <div className="text-xs text-slate-400 mt-1">across all tenants</div>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="text-sm text-amber-700 mb-1">At Limit</div>
                        <div className="text-2xl font-bold text-amber-600">{platformHealth.tenants_at_limit || 0}</div>
                        <div className="text-xs text-amber-500 mt-1">tenants need attention</div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-sm text-green-700 mb-1">Upgrade Candidates</div>
                        <div className="text-2xl font-bold text-green-600">{platformHealth.upgrade_candidates?.length || 0}</div>
                        <div className="text-xs text-green-500 mt-1">potential upsells</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                      Loading platform health data...
                    </div>
                  )}

                  {/* Upgrade Candidates List */}
                  {platformHealth?.upgrade_candidates?.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-green-600" />
                        Upgrade Opportunities
                      </h4>
                      <div className="space-y-2">
                        {platformHealth.upgrade_candidates.slice(0, 5).map((candidate) => (
                          <div key={candidate.tenant_id} className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                            <div>
                              <span className="font-medium text-slate-900">{candidate.tenant_name}</span>
                              <Badge className="ml-2 bg-slate-100 text-slate-600">
                                {getTierDisplayName(candidate.current_tier)}
                              </Badge>
                            </div>
                            <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50">
                              Contact for Upgrade
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scheduler Status */}
                  {schedulerStatus && (
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
                          <span className="text-sm text-slate-600">
                            Daily limit check: {schedulerStatus.scheduler_running ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {schedulerStatus.job_info?.next_run && (
                          <span className="text-sm text-slate-500">
                            Next run: {new Date(schedulerStatus.job_info.next_run).toLocaleString()}
                          </span>
                        )}
                        {schedulerStatus.last_execution?.completed_at && (
                          <span className="text-xs text-slate-400">
                            Last run: {new Date(schedulerStatus.last_execution.completed_at).toLocaleString()} 
                            ({schedulerStatus.last_execution.warnings_sent || 0} warnings, {schedulerStatus.last_execution.alerts_sent || 0} alerts)
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tenants Tab */}
          <TabsContent value="tenants">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search tenants..." className="pl-10 w-64" />
                </div>
              </div>
              <Button onClick={() => setShowCreateTenant(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Tenant
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tenant</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Owner</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tier</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Domain</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tenants.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <div>
                            <div className="font-medium text-slate-900">{tenant.name}</div>
                            <div className="text-sm text-slate-500">{tenant.slug}</div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-sm text-slate-600">{tenant.owner_email}</div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={getTierBadgeColor(tenant.subscription?.tier)}>
                            {getTierDisplayName(tenant.subscription?.tier)}
                          </Badge>
                          <span className="ml-2 text-xs text-slate-500">
                            {tenant.subscription?.platform_fee_percent}%
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <Badge variant={tenant.subscription?.status === 'active' ? 'default' : 'destructive'}>
                            {tenant.subscription?.status}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            {tenant.domains?.subdomain}
                            <a 
                              href={`https://${tenant.domains?.subdomain}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800"
                            >
                              <ArrowUpRight className="w-3 h-3" />
                            </a>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleSuspendTenant(tenant.id, tenant.subscription?.status)}
                              >
                                {tenant.subscription?.status === 'suspended' ? (
                                  <>
                                    <Play className="w-4 h-4 mr-2" />
                                    Reactivate
                                  </>
                                ) : (
                                  <>
                                    <Pause className="w-4 h-4 mr-2" />
                                    Suspend
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Codes Tab */}
          <TabsContent value="codes">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Select defaultValue="all">
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Codes</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="redeemed">Redeemed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => { setGeneratedCodes([]); setShowGenerateCodes(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Generate Codes
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Code</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tier</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Redeemed By</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {codes.map((code) => (
                      <tr key={code.id} className="hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
                            {code.code}
                          </code>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={getTierBadgeColor(code.tier)}>
                            {getTierDisplayName(code.tier)}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Badge variant={code.is_redeemed ? 'secondary' : 'default'}>
                            {code.is_redeemed ? 'Redeemed' : 'Available'}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-600">
                          {code.redeemed_by_tenant_id ? (
                            tenants.find(t => t.id === code.redeemed_by_tenant_id)?.name || 'Unknown'
                          ) : '-'}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(code.code)}
                            disabled={code.is_redeemed}
                          >
                            {copiedCode === code.code ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateTenant} onOpenChange={setShowCreateTenant}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Tenant</DialogTitle>
            <DialogDescription>
              Create a new white-label tenant for an agency
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTenant} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Agency Name</label>
              <Input
                value={newTenant.name}
                onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                placeholder="My Agency"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Slug (subdomain)</label>
              <Input
                value={newTenant.slug}
                onChange={(e) => setNewTenant({...newTenant, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                placeholder="my-agency"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Portal URL: {newTenant.slug || 'my-agency'}.edrano.my
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Owner Name</label>
                <Input
                  value={newTenant.owner_name}
                  onChange={(e) => setNewTenant({...newTenant, owner_name: e.target.value})}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Owner Email</label>
                <Input
                  type="email"
                  value={newTenant.owner_email}
                  onChange={(e) => setNewTenant({...newTenant, owner_email: e.target.value})}
                  placeholder="owner@agency.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Tier</label>
              <Select value={newTenant.tier} onValueChange={(v) => setNewTenant({...newTenant, tier: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tier1">Starter (5% fee, 100 clients)</SelectItem>
                  <SelectItem value="tier2">Growth (3% fee, 300 clients)</SelectItem>
                  <SelectItem value="tier3">Scale (2% fee, 500 clients)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateTenant(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Tenant</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate Codes Dialog */}
      <Dialog open={showGenerateCodes} onOpenChange={setShowGenerateCodes}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate AppSumo Codes</DialogTitle>
            <DialogDescription>
              Generate redemption codes for AppSumo customers
            </DialogDescription>
          </DialogHeader>
          
          {generatedCodes.length === 0 ? (
            <form onSubmit={handleGenerateCodes} className="space-y-4">
              <div>
                <label className="text-sm font-medium">Tier</label>
                <Select value={codeGeneration.tier} onValueChange={(v) => setCodeGeneration({...codeGeneration, tier: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tier1">Starter ($59 - 5% fee)</SelectItem>
                    <SelectItem value="tier2">Growth ($119 - 3% fee)</SelectItem>
                    <SelectItem value="tier3">Scale ($199 - 2% fee)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Number of Codes</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={codeGeneration.count}
                  onChange={(e) => setCodeGeneration({...codeGeneration, count: parseInt(e.target.value)})}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowGenerateCodes(false)}>
                  Cancel
                </Button>
                <Button type="submit">Generate</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
                Generated {generatedCodes.length} codes successfully!
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {generatedCodes.map((code, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <code className="font-mono text-sm">{code}</code>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(code)}
                    >
                      {copiedCode === code ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const text = generatedCodes.join('\n');
                    navigator.clipboard.writeText(text);
                    alert('All codes copied!');
                  }}
                >
                  Copy All
                </Button>
                <Button onClick={() => setShowGenerateCodes(false)}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
